import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, FileText } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import EmptyState from "@/components/EmptyState";

export default function PapersPage() {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      api.get("/papers", { params: q ? { q } : {} }).then((r) => setList(r.data)).finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(timer);
  }, [q]);

  return (
    <div data-testid="papers-page" className="px-6 md:px-10 lg:px-16 py-12 md:py-16">
      <div className="overline mb-3">{t("papersPage.breadcrumb")}</div>
      <h1 className="font-display font-black text-5xl md:text-6xl tracking-[-0.04em] leading-none mb-3">{t("papersPage.title")}</h1>
      <p className="text-zinc-600 max-w-2xl">{t("papersPage.intro")}</p>

      <div className="mt-12 border-t border-zinc-950 pt-6 flex items-center gap-2 border-b border-zinc-300 focus-within:border-zinc-950">
        <MagnifyingGlass size={16} className="text-zinc-400" />
        <input data-testid="paper-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("papersPage.searchPlaceholder")} className="flex-1 bg-transparent py-2 outline-none text-sm" />
      </div>

      <div className="mt-10 border border-zinc-200 bg-white">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-zinc-200 bg-[#FAFAFA] overline">
          <div className="col-span-1">№</div>
          <div className="col-span-7">{t("papersPage.colTitle")}</div>
          <div className="col-span-3 hidden md:block">{t("papersPage.colAuthors")}</div>
          <div className="col-span-1 hidden md:block text-end">{t("papersPage.colTrack")}</div>
        </div>
        {loading && list.length === 0 && (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} data-testid={`paper-skeleton-${i}`} className="border-b border-zinc-200 last:border-0 px-6 py-5">
              <div className="grid grid-cols-12 gap-4 items-start animate-pulse">
                <div className="col-span-1 h-3 bg-zinc-100 rounded" />
                <div className="col-span-12 md:col-span-7 space-y-2">
                  <div className="h-4 bg-zinc-100 rounded w-3/4" />
                  <div className="h-3 bg-zinc-100 rounded w-full" />
                  <div className="h-3 bg-zinc-100 rounded w-5/6" />
                </div>
                <div className="col-span-12 md:col-span-3 h-3 bg-zinc-100 rounded" />
                <div className="col-span-12 md:col-span-1 h-3 bg-zinc-100 rounded md:ms-auto md:w-12" />
              </div>
            </div>
          ))
        )}
        {list.map((p, idx) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
            <Link to={`/papers/${p.id}`} data-testid={`paper-row-${idx}`} className="block border-b border-zinc-200 last:border-0 px-6 py-5 hover:bg-[#FAFAFA] group">
              <div className="grid grid-cols-12 gap-4 items-start">
                <div className="col-span-1 font-mono text-xs text-zinc-400 pt-1">{String(idx + 1).padStart(3, "0")}</div>
                <div className="col-span-12 md:col-span-7">
                  <div className="font-display font-semibold text-lg tracking-tight leading-tight group-hover:text-blue-700 transition-colors">{p.title}</div>
                  <p className="mt-1.5 text-sm text-zinc-500 line-clamp-2">{p.abstract}</p>
                </div>
                <div className="col-span-12 md:col-span-3 text-xs text-zinc-600 font-mono">{(p.authors || []).join(", ")}</div>
                <div className="col-span-12 md:col-span-1 flex md:justify-end items-start gap-2">
                  {p.track && <span className="tag">{p.track}</span>}
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
        {!loading && list.length === 0 && (
          <EmptyState
            testid="papers-empty"
            icon={<FileText size={22} />}
            title={t("papersPage.empty")}
            body={t("papersPage.emptyBody")}
          />
        )}
      </div>
    </div>
  );
}
